import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Field } from "@/pages/admin/Field";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, BellRing, Send } from "lucide-react";
import { formatDate } from "@/lib/dateFormat";

const inr = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));

export const DuesSection = () => (
  <div>
    <h2 className="mb-5 font-serif text-2xl font-semibold text-gmoaa-green">Arrears & Renewals</h2>
    <Tabs defaultValue="arrears">
      <TabsList className="bg-gmoaa-green-light">
        <TabsTrigger value="arrears" className="data-[state=active]:bg-gmoaa-green data-[state=active]:text-white"><Wallet size={15} className="mr-1.5" /> Arrears</TabsTrigger>
        <TabsTrigger value="renewals" className="data-[state=active]:bg-gmoaa-green data-[state=active]:text-white"><BellRing size={15} className="mr-1.5" /> Renewals</TabsTrigger>
      </TabsList>
      <TabsContent value="arrears" className="mt-5"><ArrearsTab /></TabsContent>
      <TabsContent value="renewals" className="mt-5"><RenewalsTab /></TabsContent>
    </Tabs>
  </div>
);

const ArrearsTab = () => {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [dlgOpen, setDlgOpen] = useState(false);
  const load = () => adminApi.arrears().then((d) => { setRows(d); setSel(Object.fromEntries(d.map((r) => [r.member_id, true]))); });
  useEffect(() => { load(); }, []);

  const selectedIds = rows.filter((r) => sel[r.member_id]).map((r) => r.member_id);
  const totalOwed = rows.filter((r) => sel[r.member_id]).reduce((a, r) => a + r.amount, 0);
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div data-testid="arrears-tab">
      <p className="mb-4 text-sm text-gmoaa-text-muted">Members with fees still unpaid across all active/closed demands. Tick members and raise one draft catch-up demand for them.</p>
      {rows.length === 0 ? (
        <p className="text-sm text-gmoaa-text-muted">No outstanding dues. 🎉</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gmoaa-border-subtle bg-white p-3">
            <p className="text-sm text-gmoaa-text-muted">{selectedIds.length} selected · outstanding <span className="font-semibold text-gmoaa-saffron-hover">₹{inr(totalOwed)}</span></p>
            <Button onClick={() => setDlgOpen(true)} disabled={selectedIds.length === 0} data-testid="arrears-create-demand" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Create draft catch-up demand</Button>
          </div>
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.member_id} className="flex items-center gap-3 rounded-xl border border-gmoaa-border-subtle bg-white p-3" data-testid={`arrears-row-${r.member_id}`}>
                <Checkbox checked={!!sel[r.member_id]} onCheckedChange={() => toggle(r.member_id)} data-testid={`arrears-check-${r.member_id}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-gmoaa-text-dark">{r.name_en} <span className="text-xs font-normal text-gmoaa-text-muted">· {r.district}</span></p>
                  <p className="truncate text-xs text-gmoaa-text-muted">{r.count} unpaid {r.count === 1 ? "fee" : "fees"}{r.years.length ? ` · years ${r.years.join(", ")}` : ""}</p>
                </div>
                <span className="shrink-0 font-semibold text-gmoaa-saffron-hover">₹{inr(r.amount)}</span>
              </div>
            ))}
          </div>
        </>
      )}
      <CatchUpDialog open={dlgOpen} onOpenChange={setDlgOpen} memberIds={selectedIds} onDone={() => { setDlgOpen(false); load(); }} />
    </div>
  );
};

const CatchUpDialog = ({ open, onOpenChange, memberIds, onDone }) => {
  const thisFy = (() => { const y = new Date().getFullYear(); const m = new Date().getMonth(); const s = m >= 3 ? y : y - 1; return `${s}-${String((s + 1) % 100).padStart(2, "0")}`; })();
  const [form, setForm] = useState({ name_en: "Catch-up / Arrears", name_gu: "બાકી લેણાં", series_code: "arrears", amount: "", fy: thisFy, due_date: "" });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name_en || !form.series_code || !(Number(form.amount) > 0)) { toast.error("Name, series and a positive amount are required"); return; }
    try {
      await adminApi.createCollection({ ...form, amount: Number(form.amount), audience: { type: "members", members: memberIds } });
      toast.success(`Draft catch-up demand created for ${memberIds.length} member(s). Open Collections to activate.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create demand"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gmoaa-cream">
        <DialogHeader><DialogTitle className="text-gmoaa-green">Draft catch-up demand · {memberIds.length} member(s)</DialogTitle></DialogHeader>
        <p className="text-xs text-gmoaa-text-muted">Saved as a Draft (hidden from members). Review and Activate it from the Collections tab to notify and collect.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Fee head (English)" value={form.name_en} onChange={set("name_en")} testid="catchup-name-en" />
          <Field label="Fee head (Gujarati)" value={form.name_gu} onChange={set("name_gu")} />
          <Field label="Series code" value={form.series_code} onChange={set("series_code")} />
          <Field label="Amount (₹)" type="number" value={form.amount} onChange={set("amount")} testid="catchup-amount" />
          <Field label="Financial year" value={form.fy} onChange={set("fy")} />
          <Field label="Last date (optional)" type="date" value={form.due_date} onChange={set("due_date")} />
        </div>
        <DialogFooter><Button onClick={save} data-testid="catchup-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Create draft</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const tierColor = { 7: "bg-gmoaa-red text-white", 14: "bg-gmoaa-saffron text-white", 30: "bg-gmoaa-gold text-gmoaa-green" };

const RenewalsTab = () => {
  const [rows, setRows] = useState([]);
  const [sending, setSending] = useState(false);
  const load = () => adminApi.renewals().then(setRows);
  useEffect(() => { load(); }, []);

  const dueNow = rows.filter((r) => r.due_tier !== null);
  const send = async () => {
    setSending(true);
    try { const r = await adminApi.sendRenewals(); toast.success(`Reminders sent: ${r.sent} emailed${r.no_email ? `, ${r.no_email} had no email` : ""} (of ${r.targeted} due)`); load(); }
    catch { toast.error("Could not send reminders"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="renewals-tab">
      <p className="mb-4 text-sm text-gmoaa-text-muted">Officers with a medical registration end date. Reminders go out at 30, 14 and 7 days before expiry — each tier once. Click to email everyone currently due.</p>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gmoaa-border-subtle bg-white p-3">
        <p className="text-sm text-gmoaa-text-muted"><span className="font-semibold text-gmoaa-green">{dueNow.length}</span> due now · {rows.length} tracked</p>
        <Button onClick={send} disabled={sending || dueNow.length === 0} data-testid="renewals-send" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Send size={15} className="mr-1.5" /> {sending ? "Sending…" : "Send reminders"}</Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gmoaa-text-muted">No officers have a registration end date yet. Members can add it in their profile.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.member_id} className="flex items-center gap-3 rounded-xl border border-gmoaa-border-subtle bg-white p-3" data-testid={`renewal-row-${r.member_id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-gmoaa-text-dark">{r.name_en} <span className="text-xs font-normal text-gmoaa-text-muted">· {r.district}</span></p>
                <p className="truncate text-xs text-gmoaa-text-muted">Valid till {formatDate(r.reg_end_date)} · {r.days_left} day(s) left{!r.has_email ? " · no email on file" : ""}{r.reminders_sent.length ? ` · reminded: ${r.reminders_sent.join(", ")}d` : ""}</p>
              </div>
              {r.due_tier !== null && <Badge className={`shrink-0 ${tierColor[r.due_tier] || ""}`}>{r.due_tier}-day due</Badge>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
